// Fix: Populated file with ArtistCard component
import React from 'react';
import { Artist, UserRole, Project, canEditArtists, canDeleteArtists, canViewArtistContacts } from '../types';
import { ICONS } from '../constants';
import { getAvatarColor, getInitials, getAvatarUrl } from '../utils/avatarUtils';
import { getProjectBadgeColor } from '../utils/projectUtils';
import { formatCardDateRange } from '../utils/dateUtils';
import { useTranslation } from '../hooks/useTranslation';
import Button from './ui/Button';

interface ArtistCardProps {
  artist: Artist;
  projects: Project[];
  currentRole: string | null;
  onEdit: (artist: Artist) => void;
  onDelete?: (artistId: string) => void; // Опциональный для модальных окон
  isSelected: boolean;
  onSelect: (event: React.MouseEvent) => void;
}

const getAvailabilityStatus = (availability: string[], t: (key: string) => string): { status: string; className: string } => {
  if (availability.length === 0) {
    return { status: t('artistCard.noAvailability'), className: 'text-gray-500' };
  }
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];

  // Filter and normalize dates to YYYY-MM-DD format
  const validDates = availability
    .map(dateStr => {
      // Handle various date formats
      if (typeof dateStr !== 'string') return null;
      
      // If it's already in YYYY-MM-DD format
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      
      // If it's ISO format with time
      if (dateStr.includes('T') || dateStr.includes('Z')) {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        }
      }
      
      return null;
    })
    .filter((date): date is string => date !== null);

  if (validDates.length === 0) {
    return { status: t('artistCard.noAvailability'), className: 'text-gray-500' };
  }

  const sortedDates = validDates.sort();
  
  // Найти самую раннюю и самую позднюю даты
  const earliestDate = sortedDates[0];
  const latestDate = sortedDates[sortedDates.length - 1];
  
  if (!earliestDate) {
    return { status: t('artistCard.noAvailability'), className: 'text-gray-500' };
  }

  // Если самая поздняя дата < сегодня, информация устарела
  if (latestDate < todayStr) {
    return { status: t('artistCard.needsUpdate'), className: 'text-orange-500' };
  }

  // Если самая ранняя дата <= сегодня <= самая поздняя дата, доступен сейчас
  if (earliestDate <= todayStr && todayStr <= latestDate) {
    // Показываем диапазон, если есть несколько дат
    if (sortedDates.length > 1 && earliestDate !== latestDate) {
      const startDate = new Date(earliestDate + 'T00:00:00');
      const endDate = new Date(latestDate + 'T00:00:00');
      const locale = t('common.locale') || 'en-US';
      return { 
        status: `${t('artistCard.availableNow')} (${t('artistCard.freeFrom')} ${startDate.toLocaleDateString(locale)} ${t('artistCard.freeTo')} ${endDate.toLocaleDateString(locale)})`, 
        className: 'text-green-500' 
      };
    }
    return { status: t('artistCard.availableNow'), className: 'text-green-500' };
  }

  // Если самая ранняя дата > сегодня, будет доступен с этой даты
  if (earliestDate > todayStr) {
    const startDate = new Date(earliestDate + 'T00:00:00');
    if (sortedDates.length > 1 && earliestDate !== latestDate) {
      const endDate = new Date(latestDate + 'T00:00:00');
      const locale = t('common.locale') || 'en-US';
      return { 
        status: `${t('artistCard.freeFrom')} ${startDate.toLocaleDateString(locale)} ${t('artistCard.freeTo')} ${endDate.toLocaleDateString(locale)}`, 
        className: 'text-yellow-500' 
      };
    }
    const locale = t('common.locale') || 'en-US';
    return { status: `${t('artistCard.freeFrom')} ${startDate.toLocaleDateString(locale)}`, className: 'text-yellow-500' };
  }

  return { status: t('artistCard.noAvailability'), className: 'text-gray-500' };
};


// Helper: Check if project is active (current date is within project date range)
const isProjectActive = (project: Project): boolean => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = new Date(project.startDate);
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(project.endDate);
    endDate.setHours(23, 59, 59, 999);
    
    return today >= startDate && today <= endDate;
  } catch {
    return false;
  }
};

const ArtistCard: React.FC<ArtistCardProps> = ({ artist, projects, currentRole, onEdit, onDelete, isSelected, onSelect }) => {
  const { t } = useTranslation();
  const [avatarLoading, setAvatarLoading] = React.useState(false);
  const [avatarError, setAvatarError] = React.useState(false);
  const [clickTimeout, setClickTimeout] = React.useState<NodeJS.Timeout | null>(null);
  const availability = getAvailabilityStatus(artist.availability, t);
  
  // Only show active projects (current date within project date range)
  const assignedProjects = artist.assignedProjects
    .map(pId => projects.find(p => p.id === pId))
    .filter(Boolean) as Project[];
  const activeProjects = assignedProjects.filter(isProjectActive);

  // Double-click handler for editing
  const handleCardClick = (event: React.MouseEvent) => {
    if (clickTimeout) {
      // Double click detected
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      
      if (canEditArtists(currentRole)) {
        onEdit(artist);
      }
    } else {
      // Single click - set timeout
      const timeout = setTimeout(() => {
        onSelect(event);
        setClickTimeout(null);
      }, 300);
      setClickTimeout(timeout);
    }
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
    };
  }, [clickTimeout]);

  const cardClasses = `
    bg-gray-800 rounded-lg shadow-lg p-6 flex flex-col justify-between 
    transition-all hover:shadow-indigo-500/20 hover:scale-[1.02] cursor-pointer
    min-h-[600px]
    ${isSelected ? 'ring-2 ring-indigo-500' : 'ring-2 ring-transparent'}
  `;

  // Debug logging and avatar state management
  React.useEffect(() => {
    if (artist.name === 'Test Artist' || !artist.avatarUrl) {
      console.log('🖼️ [ArtistCard] Avatar debug:', {
        name: artist.name,
        avatarUrl: artist.avatarUrl,
        hasAvatar: !!artist.avatarUrl
      });
    }
    
    // Reset avatar states when artist changes
    if (artist.avatarUrl) {
      setAvatarLoading(true);
      setAvatarError(false);
    } else {
      setAvatarLoading(false);
      setAvatarError(false);
    }
  }, [artist.avatarUrl, artist.name]);

  return (
    <div className={cardClasses} onClick={handleCardClick} data-artist-id={artist.id}>
      <div>
        <div className="flex flex-col items-center text-center">
        {(() => {
          const avatarUrl = getAvatarUrl(artist.avatarUrl);
          return avatarUrl && !avatarError ? (
            <div className="relative w-24 h-24 mb-4">
              {avatarLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-700 rounded-full border-2 border-gray-600">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
                </div>
              )}
              <img 
                src={avatarUrl}
                alt={artist.name} 
                className={`w-24 h-24 rounded-full object-cover border-2 border-gray-700 pointer-events-none transition-opacity duration-200 ${avatarLoading ? 'opacity-0' : 'opacity-100'}`}
                onError={(e) => {
                  console.log('❌ [ArtistCard] Avatar failed to load:', avatarUrl);
                  setAvatarError(true);
                  setAvatarLoading(false);
                }}
                onLoad={() => {
                  console.log('✅ [ArtistCard] Avatar loaded successfully:', avatarUrl);
                  setAvatarLoading(false);
                  setAvatarError(false);
                }}
              />
            </div>
          ) : null;
        })()}
          <div 
            className={`w-24 h-24 rounded-full bg-gradient-to-br ${getAvatarColor(artist.name)} flex items-center justify-center mb-4 border-2 border-gray-700 pointer-events-none ${artist.avatarUrl && !avatarError ? 'hidden' : ''}`}
          >
            <span className="text-white text-2xl font-bold avatar-text">
              {getInitials(artist.name)}
            </span>
          </div>
          <h3 className="text-xl font-bold text-white">{artist.name}</h3>
          {artist.registrationStatus === 'pending' && (
            <span className="mt-1 inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-600/30 text-yellow-300 border border-yellow-500/50">
              {t('profile.access.pending')}
            </span>
          )}
          {artist.registrationStatus === 'active' && artist.profileType && artist.profileType !== 'vfx_artist' && (
            <span className="mt-1 inline-block px-2 py-0.5 text-xs rounded-full bg-gray-700 text-gray-300">
              {t(`register.types.${artist.profileType}`)}
            </span>
          )}
          {artist.location && (
            <p className="text-sm text-gray-300 mt-1 flex items-center justify-center">
              <ICONS.MapPin className="w-4 h-4 mr-1" />
              {artist.location}
            </p>
          )}
          {artist.description && (
            <p className="text-sm text-gray-400 mt-2 text-center line-clamp-5" title={artist.description}>{artist.description}</p>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-700 space-y-3">
          <div>
            <h4 className="text-sm font-medium text-gray-400 text-center">{t('artistCard.activeProjects')}</h4>
            <div className="flex flex-wrap gap-2 mt-1 justify-center">
              {activeProjects.length > 0 ? (
                activeProjects.map(p => (
                  <div key={p.id} className={`px-2 py-1 ${getProjectBadgeColor(p.id)} text-xs font-bold text-white rounded-full text-center`}>
                    {p.name} <span className="font-normal opacity-75">{formatCardDateRange(p.startDate, p.endDate)}</span>
                  </div>
                ))
              ) : (
                <span className="text-xs text-gray-500 italic">{t('artistCard.noActiveProjects')}</span>
              )}
            </div>
          </div>
          <div>
            <div className="flex flex-wrap gap-2 mt-1 justify-center">
              {artist.software.map(s => (
                <span key={s} className="px-2 py-1 bg-gray-700 text-xs font-medium text-gray-300 rounded-full">{s}</span>
              ))}
            </div>
          </div>
           {/* Contacts section - hidden for Demo users */}
           {canViewArtistContacts(currentRole) ? (
             <div className="text-center">
              <h4 className="text-sm font-medium text-gray-400">{t('artistCard.email')} / {t('artistCard.telegram')}</h4>
               <div className="space-y-1">
                  {artist.email ? (
                    <p className="text-xs text-gray-300">📧 {artist.email}</p>
                  ) : artist.telegram ? (
                    <p className="text-xs text-gray-300">📱 {artist.telegram}</p>
                  ) : (
                    <p className="text-xs text-gray-500 italic">{t('artistCard.noContact')}</p>
                  )}
                  {artist.telegram && artist.email && (
                    <p className="text-xs text-gray-300">📱 {artist.telegram}</p>
                  )}
                  {artist.portfolioUrl ? (
                    <div className="flex items-center justify-center space-x-1">
                      <ICONS.Link className="w-3 h-3 text-gray-400" />
                      <a href={artist.portfolioUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-indigo-400" onClick={(e) => e.stopPropagation()}>
                        {t('artistCard.portfolio')}
                      </a>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 italic">{t('artistCard.noPortfolio')}</p>
                  )}
               </div>
            </div>
           ) : (
             <div className="text-center">
              <h4 className="text-sm font-medium text-gray-400">{t('artistCard.portfolio')}</h4>
               <div className="space-y-1">
                  {artist.portfolioUrl ? (
                    <div className="flex items-center justify-center space-x-1">
                      <ICONS.Link className="w-3 h-3 text-gray-400" />
                      <a href={artist.portfolioUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-400 hover:text-indigo-400" onClick={(e) => e.stopPropagation()}>
                        {t('artistCard.portfolio')}
                      </a>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 italic">{t('artistCard.noPortfolio')}</p>
                  )}
               </div>
            </div>
           )}
          <div className="text-center">
            <h4 className="text-sm font-medium text-gray-400">{t('artistCard.availability')}</h4>
            <p className={`text-sm font-semibold ${availability.className}`}>{availability.status}</p>
          </div>
        </div>
      </div>
      
      {canEditArtists(currentRole) && (
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-400 italic">{t('artistCard.doubleClickToEdit')}</p>
        </div>
      )}
      
      {canDeleteArtists(currentRole) && onDelete && (
        <div className="mt-4 flex justify-end">
          <Button variant="danger" onClick={(e) => { e.stopPropagation(); onDelete(artist.id); }} className="!p-2">
            <ICONS.Trash className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default ArtistCard;